import type { ResourceLink, ResourcePageRow } from '@routeback/core';

/** The two pages every company starts with (server: private.seed_resource_pages). */
export function defaultResourcePages(): Pick<ResourcePageRow, 'slug' | 'title' | 'body_md' | 'links' | 'sort_order'>[] {
  return [
    {
      slug: 'benefits', title: 'Benefits & pay', sort_order: 1,
      body_md: 'Your pay stubs, health insurance, retirement account and tax resources.',
      links: [{
        id: 'irs', label: 'IRS Tax Withholding Estimator',
        desc: 'Check your tax withholding, especially if you had other wages earlier this year.',
        url: 'https://www.irs.gov/individuals/tax-withholding-estimator',
      }],
    },
    { slug: 'resources', title: 'Driver resources', sort_order: 2, body_md: 'Important guides and documents. Tap one to open it.', links: [] },
  ];
}

/** Live pages in display order. */
export function sortPages(pages: ResourcePageRow[]): ResourcePageRow[] {
  return pages.filter((p) => !p.deleted_at).sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title));
}

/** Where the handbook and consent cards go: the "resources" page, else the last page. */
export function handbookPageId(pages: ResourcePageRow[]): string | null {
  const live = sortPages(pages);
  return (live.find((p) => p.slug === 'resources') ?? live[live.length - 1])?.id ?? null;
}

/**
 * Tidy what a manager typed into a link: bare domains get https://, email
 * addresses get mailto:, phone numbers tel:. Returns null when it isn't a
 * web/email/phone address (the server refuses anything else too).
 */
export function normalizeUrl(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (/^(https?:\/\/)\S+$/i.test(s)) return s;
  if (/^mailto:\S+@\S+$/i.test(s)) return s;
  if (/^tel:[0-9+() .-]+$/i.test(s)) return s;
  if (/^[^\s@/]+@[^\s@]+\.[a-z]{2,}$/i.test(s)) return `mailto:${s}`;
  const digits = s.replace(/\D/g, '');
  if (/^[0-9+() .-]+$/.test(s) && digits.length >= 7 && digits.length <= 15) return `tel:${s.replace(/[^0-9+]/g, '')}`;
  if (/^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(s)) return `https://${s}`;
  return null;
}

/** What to show under a card: "vestwell.com", "hr@acme.com", "630-555-0100", "PDF". */
export function linkCaption(l: ResourceLink): string {
  if (l.file_path) {
    const ext = l.file_path.split('.').pop()?.toLowerCase() ?? '';
    return ext === 'pdf' ? 'PDF' : ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext) ? 'Image' : 'File';
  }
  const u = l.url ?? '';
  if (u.startsWith('mailto:')) return u.slice(7);
  if (u.startsWith('tel:')) return u.slice(4);
  try {
    return new URL(u).hostname.replace(/^www\./, '');
  } catch {
    return u;
  }
}

/** Move one link up (-1) or down (+1); returns a new array. */
export function moveLink(links: ResourceLink[], id: string, dir: -1 | 1): ResourceLink[] {
  const i = links.findIndex((l) => l.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= links.length) return links;
  const next = links.slice();
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/** A slug for a new page from its title, unique among `taken`. */
export function slugFor(title: string, taken: string[]): string {
  const base = title.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'page';
  let s = base;
  for (let n = 2; taken.includes(s); n++) s = `${base}-${n}`;
  return s;
}

export type IntroPart = { text: string; href?: string };
/**
 * The intro is plain text: blank lines start a new paragraph and web
 * addresses become links. No HTML is ever rendered from it.
 */
export function introParagraphs(body: string): IntroPart[][] {
  return body
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const parts: IntroPart[] = [];
      const re = /(https?:\/\/[^\s)]+[^\s).,!?;:])/g;
      let last = 0;
      for (let m = re.exec(p); m; m = re.exec(p)) {
        if (m.index > last) parts.push({ text: p.slice(last, m.index) });
        parts.push({ text: m[1]!, href: m[1]! });
        last = m.index + m[1]!.length;
      }
      if (last < p.length) parts.push({ text: p.slice(last) });
      return parts;
    });
}
